import type {
  CellClassParams,
  ColDef,
  ExcelStyle,
  HeaderClassParams,
  IHeaderParams
} from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getConditionalFormattingCellStyle } from "./apply-format-rules";
import {
  buildConditionalFormattingCellClassRules,
  buildConditionalFormattingExcelStyles,
  buildConditionalFormattingInheritCellClassRules
} from "./excel-styles";
import {
  CONDITIONAL_FORMATTING_APPLIED_HEADER_CLASS,
  conditionalFormattingHeaderTemplate
} from "./header-icon";
import { normalizeConditionalFormattingRules } from "./normalize-rules";
import type { ConditionalFormattingRule } from "./types";

type UseConditionalFormattingParams<TData> = {
  gridRef: React.RefObject<AgGridReact<TData>> | null;
  defaultColDef: ColDef<TData>;
  initialRules?: ConditionalFormattingRule[];
  /** Print/PDF grids render the formatting itself, not the editing affordance. */
  showAppliedHeaderIcon?: boolean;
};

const toClassList = (value: string | string[] | undefined | null): string[] => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

export const useConditionalFormatting = <TData = any>({
  gridRef,
  defaultColDef,
  initialRules = [],
  showAppliedHeaderIcon = true
}: UseConditionalFormattingParams<TData>) => {
  const [rules, setRules] = useState<ConditionalFormattingRule[]>(() =>
    normalizeConditionalFormattingRules(initialRules)
  );
  const setConditionalFormattingRules = useCallback(
    (nextRules: ConditionalFormattingRule[]) =>
      setRules(normalizeConditionalFormattingRules(nextRules)),
    []
  );

  const conditionalFormattingExcelStyles = useMemo<ExcelStyle[]>(
    () => buildConditionalFormattingExcelStyles(rules),
    [rules]
  );

  useEffect(() => {
    const gridApi = gridRef?.current?.api;
    gridApi?.refreshCells?.({ force: true });
    // AG Grid merges `cellStyle` results into existing cell DOM styles.
    // Recreating rows ensures background/font styles from a deleted rule are
    // removed instead of remaining as stale inline styles.
    gridApi?.redrawRows?.();
    // `headerClass` is only re-evaluated when headers are rebuilt.
    gridApi?.refreshHeader?.();
  }, [gridRef, rules]);

  const formattedColumnIds = useMemo(
    () => new Set(rules.map((rule) => rule.columnId)),
    [rules]
  );

  const formattedDefaultColDef = useMemo<ColDef<TData>>(() => {
    const baseCellStyle = defaultColDef.cellStyle;
    const baseCellClassRules = defaultColDef.cellClassRules;
    const baseHeaderClass = defaultColDef.headerClass;
    const cfCellClassRules = {
      ...buildConditionalFormattingCellClassRules<TData>(
        rules,
        conditionalFormattingExcelStyles.map((style) => style.id)
      ),
      ...buildConditionalFormattingInheritCellClassRules<TData>(rules)
    };

    const isFormatted = (columnId: string | undefined) =>
      !!columnId && formattedColumnIds.has(columnId);

    const headerClass = (params: HeaderClassParams<TData>) => {
      const baseClasses = toClassList(
        typeof baseHeaderClass === "function"
          ? baseHeaderClass(params)
          : baseHeaderClass
      );
      if (!isFormatted(params.column?.getColId?.())) return baseClasses;
      return [...baseClasses, CONDITIONAL_FORMATTING_APPLIED_HEADER_CLASS];
    };

    const baseHeaderComponentParams = defaultColDef.headerComponentParams;
    // Only formatted columns get a template, so every other header keeps
    // AG Grid's own markup untouched.
    const headerComponentParams = (params: IHeaderParams<TData>) => {
      const base =
        typeof baseHeaderComponentParams === "function"
          ? baseHeaderComponentParams(params)
          : baseHeaderComponentParams;
      if (base) return base;
      return isFormatted(params.column?.getColId?.())
        ? conditionalFormattingHeaderTemplate()
        : {};
    };

    return {
      ...defaultColDef,
      menuTabs: ["filterMenuTab", "generalMenuTab"],
      cellStyle: (params: CellClassParams<TData>) =>
        getConditionalFormattingCellStyle(params, rules) ??
        (typeof baseCellStyle === "function"
          ? baseCellStyle(params)
          : baseCellStyle),
      // CF rules last so they coexist with column-level rules when AG Grid
      // recreates columns from an updated defaultColDef.
      cellClassRules: {
        ...baseCellClassRules,
        ...cfCellClassRules
      },
      ...(showAppliedHeaderIcon ? { headerClass, headerComponentParams } : {})
    };
  }, [
    conditionalFormattingExcelStyles,
    defaultColDef,
    formattedColumnIds,
    rules,
    showAppliedHeaderIcon
  ]);

  return {
    conditionalFormattingRules: rules,
    setConditionalFormattingRules,
    conditionalFormattingDefaultColDef: formattedDefaultColDef,
    conditionalFormattingExcelStyles
  };
};
